

import { useRef, useState, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';
import { FaUtensils, FaChartBar, FaUser, FaVenusMars, 
  FaRulerVertical, FaWalking, FaCalculator, FaFilePdf ,
  FaUserTie, FaLaptop,  FaMedal,  FaHammer,  FaBook, 
  FaUmbrellaBeach, FaInfoCircle,  FaQuestionCircle, 
  FaRunning, FaCouch, FaTrophy, FaExclamationTriangle, FaAtom, FaBriefcase, FaBookMedical} from 'react-icons/fa';
import ClipLoader from 'react-spinners/ClipLoader';
import { motion } from "framer-motion";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FaWeightScale } from 'react-icons/fa6';
import './App.css';
import InfoIcon from './InfoIcon';
import logoImage from './nvglogo1.png';



function NutritionalCalculator() {
  // State variables
  const [age, setAge] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [gender, setGender] = useState('male');
  const [pregnancyStatus, setPregnancyStatus] = useState('none');
  const [calculated, setCalculated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [name, setName] = useState("");
  const [isPressed, setIsPressed] = useState(false);
  const [exportPressed, setExportPressed] = useState(false);
  const [calculatePressed, setCalculatePressed] = useState(false);
  const [weight, setWeight] = useState(70);
  const [height, setHeight] = useState(170);
  const [lifestyle, setLifestyle] = useState('officeWorker');
  const [activityLevel, setActivityLevel] = useState('moderate');
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [exportHovered, setExportHovered] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [hoverStates, setHoverStates] = useState({
    formFields: {
      nameInput: false,
      ageInput: false,
      weightInput: false,
      heightInput: false,
      genderInput: false,
      pregnancyInput: false,
      trimesterInput: false,
      postpartumInput: false,
      activityInput: false
    }
  });
  const printRef = useRef();

  const exportPDF = async () => {
    const input = printRef.current;

    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      windowWidth: input.scrollWidth
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save('Nutrition_Report.pdf');
  };

  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowEmailForm(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    console.log('Debug - Current state:', {
      calculated,
      isLoading,
      nameValid: name.length >= 3
    });
  }, [calculated, isLoading, name]); // Only runs when these specific states change

  

  const pdfRef = useRef();

  // Lifestyle options
  const lifestyleOptions = [
    { value: 'officeWorker', label: 'Office Worker' },
    { value: 'athlete', label: 'Athlete' },
    { value: 'manualLaborer', label: 'Manual Laborer' },
    { value: 'student', label: 'Student' },
    { value: 'retired', label: 'Retired' }
  ];

  // Activity level options
  const activityLevelOptions = [
    { value: 'sedentary', label: 'Sedentary (little or no exercise)' },
    { value: 'moderate', label: 'Moderate (exercise 3-5 days/week)' },
    { value: 'high', label: 'High (intense exercise 6-7 days/week)' },
    { value: 'extreme', label: 'Extreme (very intense exercise daily)' }
  ];

  // USDA Nutritional Data with weight adjustments
  const nutritionalData = {
    male: {
      ageRanges: [
        { min: 1, max: 3, label: '1-3 years' },
        { min: 4, max: 8, label: '4-8 years' },
        { min: 9, max: 13, label: '9-13 years' },
        { min: 14, max: 18, label: '14-18 years' },
        { min: 19, max: 30, label: '19-30 years' },
        { min: 31, max: 50, label: '31-50 years' },
        { min: 51, max: 70, label: '51-70 years' },
        { min: 71, max: 120, label: '71+ years' }
      ],
      vitamins: {
        'Vitamin A (mcg RAE)': [300, 400, 600, 900, 900, 900, 900, 900],
        'Vitamin C (mg)': [15, 25, 45, 75, 90, 90, 90, 90],
        'Vitamin D (IU)': [600, 600, 600, 600, 600, 600, 600, 800],
        'Vitamin E (mg)': [6, 7, 11, 15, 15, 15, 15, 15],
        'Vitamin K (mcg)': [30, 55, 60, 75, 120, 120, 120, 120],
        'Thiamine (mg)': [0.5, 0.6, 0.9, 1.2, 1.2, 1.2, 1.2, 1.2],
        'Riboflavin (mg)': [0.5, 0.6, 0.9, 1.3, 1.3, 1.3, 1.3, 1.3],
        'Niacin (mg)': [6, 8, 12, 16, 16, 16, 16, 16],
        'Vitamin B6 (mg)': [0.5, 0.6, 1.0, 1.3, 1.3, 1.3, 1.7, 1.7],
        'Folate (mcg DFE)': [150, 200, 300, 400, 400, 400, 400, 400],
        'Vitamin B12 (mcg)': [0.9, 1.2, 1.8, 2.4, 2.4, 2.4, 2.4, 2.4],
        'Choline (mg)': [200, 250, 375, 550, 550, 550, 550, 550]
      },
      minerals: {
        'Calcium (mg)': [700, 1000, 1300, 1300, 1000, 1000, 1000, 1200],
        'Iron (mg)': [7, 10, 8, 11, 8, 8, 8, 8],
        'Magnesium (mg)': [80, 130, 240, 410, 400, 420, 420, 420],
        'Phosphorus (mg)': [460, 500, 1250, 1250, 700, 700, 700, 700],
        'Potassium (mg)': [2000, 2300, 2500, 3000, 3400, 3400, 3400, 3400],
        'Sodium (mg)': [1500, 1900, 2200, 2300, 1500, 1500, 1300, 1200],
        'Zinc (mg)': [3, 5, 8, 11, 11, 11, 11, 11],
        'Selenium (mcg)': [20, 30, 40, 55, 55, 55, 55, 55],
        'Copper (mcg)': [340, 440, 700, 890, 900, 900, 900, 900],
        'Manganese (mg)': [1.2, 1.5, 1.9, 2.2, 2.3, 2.3, 2.3, 2.3],
        'Chromium (mcg)': [0.2, 5.5, 11, 25, 35, 35, 30, 30],
        'Molybdenum (mcg)': [17, 22, 34, 43, 45, 45, 45, 45],
        'Chloride (mg)': [1500, 1900, 2300, 2300, 2300, 2300, 2000, 1800],
        'Omega-3 DHA (mg)': [70, 90, 120, 250, 300, 300, 300, 300],
      },
      weightBased: {
        'Protein (g)': [1.05, 1.05, 1.05, 1.05, 0.8, 0.8, 0.8, 0.8],
        'Carbohydrates (g)': [3.0, 3.0, 3.5, 4.0, 4.5, 4.5, 4.0, 3.5],
        'Fats (g)': [0.8, 0.8, 0.9, 1.0, 1.1, 1.1, 1.0, 0.9],
        
        'Water (ml)': [100, 100, 100, 100, 35, 35, 35, 35]
         // General male
        
      }
    },
    female: {
      ageRanges: [
        { min: 1, max: 3, label: '1-3 years' },
        { min: 4, max: 8, label: '4-8 years' },
        { min: 9, max: 13, label: '9-13 years' },
        { min: 14, max: 18, label: '14-18 years' },
        { min: 19, max: 30, label: '19-30 years' },
        { min: 31, max: 50, label: '31-50 years' },
        { min: 51, max: 70, label: '51-70 years' },
        { min: 71, max: 120, label: '71+ years' }
      ],
      vitamins: {
        'Vitamin A (mcg RAE)': [300, 400, 600, 700, 700, 700, 700, 700],
        'Vitamin C (mg)': [15, 25, 45, 65, 75, 75, 75, 75],
        'Vitamin D (IU)': [600, 600, 600, 600, 600, 600, 600, 800],
        'Vitamin E (mg)': [6, 7, 11, 15, 15, 15, 15, 15],
        'Vitamin K (mcg)': [30, 55, 60, 75, 90, 90, 90, 90],
        'Thiamine (mg)': [0.5, 0.6, 0.9, 1.0, 1.1, 1.1, 1.1, 1.1],
        'Riboflavin (mg)': [0.5, 0.6, 0.9, 1.0, 1.1, 1.1, 1.1, 1.1],
        'Niacin (mg)': [6, 8, 12, 14, 14, 14, 14, 14],
        'Vitamin B6 (mg)': [0.5, 0.6, 1.0, 1.2, 1.3, 1.3, 1.5, 1.5],
        'Folate (mcg DFE)': [150, 200, 300, 400, 400, 400, 400, 400],
        'Vitamin B12 (mcg)': [0.9, 1.2, 1.8, 2.4, 2.4, 2.4, 2.4, 2.4],
        'Choline (mg)': [200, 250, 375, 400, 425, 425, 425, 425]
      },
      minerals: {
        'Calcium (mg)': [700, 1000, 1300, 1300, 1000, 1000, 1200, 1200],
        'Iron (mg)': [7, 10, 8, 15, 18, 18, 8, 8],
        'Magnesium (mg)': [80, 130, 240, 360, 310, 320, 320, 320],
        'Phosphorus (mg)': [460, 500, 1250, 1250, 700, 700, 700, 700],
        'Potassium (mg)': [2000, 2300, 2300, 2300, 2600, 2600, 2600, 2600],
        'Sodium (mg)': [1500, 1900, 2200, 2300, 1500, 1500, 1300, 1200],
        'Zinc (mg)': [3, 5, 8, 9, 8, 8, 8, 8],
        'Selenium (mcg)': [20, 30, 40, 55, 55, 55, 55, 55],
        'Copper (mcg)': [340, 440, 700, 890, 900, 900, 900, 900],
        'Manganese (mg)': [1.2, 1.5, 1.6, 1.6, 1.8, 1.8, 1.8, 1.8],
        'Chromium (mcg)': [0.2, 5.5, 21, 24, 25, 25, 20, 20],
        'Molybdenum (mcg)': [17, 22, 34, 43, 45, 45, 45, 45],
        'Chloride (mg)': [1500, 1900, 2300, 2300, 2300, 2300, 2000, 1800],
        'Omega-3 DHA (mg)': [70, 90, 120, 250, 250, 250, 250, 250],
      },
      weightBased: {
        'Protein (g)': [1.05, 1.05, 1.05, 1.05, 0.8, 0.8, 0.8, 0.8],
        'Carbohydrates (g)': [3.0, 3.0, 3.5, 3.8, 4.0, 4.0, 3.8, 3.5],
        'Fats (g)': [0.8, 0.8, 0.9, 1.0, 1.0, 1.0, 0.9, 0.8],
        
        'Water (ml)': [100, 100, 100, 100, 35, 35, 35, 35]
      },
      pregnancy: {
        vitamins: {
          'Vitamin A (mcg RAE)': [400, 400, 600, 750, 770, 770, 770, 770],
          'Vitamin C (mg)': [15, 25, 45, 80, 85, 85, 85, 85],
          'Vitamin D (IU)': [600, 600, 600, 600, 600, 600, 600, 800],
          'Folate (mcg DFE)': [150, 200, 300, 600, 600, 600, 600, 600],
          'Iron (mg)': [7, 10, 8, 27, 27, 27, 27, 27]
        },
        minerals: {
          'Calcium (mg)': [700, 1000, 1300, 1300, 1000, 1000, 1200, 1200],
          'Zinc (mg)': [3, 5, 8, 12, 11, 11, 11, 11],
          'Iodine (mcg)': [90, 90, 120, 220, 220, 220, 220, 220],
          'Omega-3 DHA (mg)': [null, null, null, null, 300, 300, 300, 300],
        },
        weightBased: {
          'Protein (g)': [1.2, 1.2, 1.2, 1.2, 1.1, 1.1, 1.1, 1.1],
          'Carbohydrates (g)': [3.2, 3.2, 3.7, 4.2, 4.7, 4.7, 4.2, 3.7],
          'Fats (g)': [1.0, 1.0, 1.1, 1.2, 1.3, 1.3, 1.2, 1.1],
          
          'Water (ml)': [110, 110, 110, 110, 40, 40, 40, 40]
        }
      },
      lactation: {
        vitamins: {
          'Vitamin A (mcg RAE)': [400, 400, 600, 1200, 1300, 1300, 1300, 1300],
          'Vitamin C (mg)': [15, 25, 45, 115, 120, 120, 120, 120],
          'Vitamin D (IU)': [600, 600, 600, 600, 600, 600, 600, 800],
          'Folate (mcg DFE)': [150, 200, 300, 500, 500, 500, 500, 500]
        },
        minerals: {
          'Calcium (mg)': [700, 1000, 1300, 1300, 1000, 1000, 1200, 1200],
          'Zinc (mg)': [3, 5, 8, 13, 12, 12, 12, 12],
          'Omega-3 DHA (mg)': [null, null, null, null, 300, 300, 300, 300],
        },
        weightBased: {
          'Protein (g)': [1.3, 1.3, 1.3, 1.3, 1.2, 1.2, 1.2, 1.2],
          'Carbohydrates (g)': [3.5, 3.5, 4.0, 4.5, 5.0, 5.0, 4.5, 4.0],
          'Fats (g)': [1.1, 1.1, 1.2, 1.3, 1.4, 1.4, 1.3, 1.2],
          
          'Water (ml)': [120, 120, 120, 120, 45, 45, 45, 45]
        }
      }
    }
  };

  // Activity level multipliers
  const activityMultipliers = {
    sedentary: 1.0,
    moderate: 1.2,
    high: 1.45,
    extreme: 1.7
  };

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleHover = (section, field, isHovering) => {
    setHoverStates(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: isHovering
      }
    }));
  };

  const getAgeIndex = () => {
    const ranges = nutritionalData[gender].ageRanges;
    for (let i = 0; i < ranges.length; i++) {
      if (age >= ranges[i].min && age <= ranges[i].max) {
        return i;
      }
    }
    return ranges.length - 1;
  };
  

  const calculateRequirements = () => {
    // Add validation
    if (name.length < 3) return;
    
    setIsLoading(true);
    
    // Simulate calculation delay
    setTimeout(() => {
      setCalculated(true);
      setIsLoading(false);
    }, 500);
  };

  const resetForm = () => {
    setCalculated(false);
    setAge(30);
    setGender('male');
    setPregnancyStatus('none');
    setWeight(70);
    setHeight(170);
    setLifestyle('officeWorker');
    setActivityLevel('moderate');
  };

  const getValue = (category, nutrient) => {
    const ageIndex = getAgeIndex();
    let baseValue = nutritionalData[gender][category]?.[nutrient]?.[ageIndex] || 0;
  
    // Check for pregnancy/lactation overrides
    if (gender === 'female' && pregnancyStatus !== 'none') {
      const pregnancyValue = nutritionalData[gender][pregnancyStatus][category]?.[nutrient]?.[ageIndex];
      if (pregnancyValue !== undefined && pregnancyValue !== null) {
        baseValue = pregnancyValue;
      }
    }
  
    if (category === 'weightBased') {
      // ✅ Skip multiplication for fixed nutrients like DHA
      if (nutrient === 'Omega-3 DHA (mg)') {
        return baseValue; // No multiplication
      }
  
      let result = baseValue * weight;
  
      // Add 500ml for lactating women only for water
      if (nutrient === 'Water (ml)' && pregnancyStatus === 'lactation') {
        result += 500;
      }
  
      // Apply multiplier to Protein and Water
      if (nutrient === 'Protein (g)' || nutrient === 'Water (ml)') {
        return Math.round(result * activityMultipliers[activityLevel]);
      }
  
      return Math.round(result);
    }
  
    return baseValue;
  };
  

  const getAgeGroupLabel = () => {
    const ageIndex = getAgeIndex();
    return nutritionalData[gender].ageRanges[ageIndex].label;
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      alert('Please enter a valid email address');
      return;
    }
  
    setIsSavingEmail(true);
    
    try {
      await fetch('your-api-endpoint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          reportData: { 
            name, 
            age, 
            weight,
            height,
            gender,
            pregnancyStatus,
            lifestyle,
            activityLevel
          } 
        })
      });
      
      setShowEmailForm(false);
      await handlePrint();
    } catch (error) {
      console.error("Error saving email:", error);
      alert('Failed to save email. You can still export without saving.');
    } finally {
      setIsSavingEmail(false);
    }
  };
 
  
  const handlePrint = async () => {
    setIsExporting(true);
    let printContainer = null;
    
    try {
      const element = pdfRef.current;
      if (!element) {
        throw new Error('PDF reference element not found');
      }
  
      // Create print container
      printContainer = document.createElement('div');
      printContainer.style.position = 'fixed';
      printContainer.style.top = '-9999px';
      printContainer.style.left = '0';
      printContainer.style.width = '210mm';
      printContainer.style.backgroundColor = 'white';
      document.body.appendChild(printContainer);
  
      // Clone and organize content
      const clone = element.cloneNode(true);
      clone.querySelectorAll('.no-print, .action-buttons').forEach(el => el.remove());
  
      // Get sections - with null checks
      const header = clone.querySelector('.header');
      if (!header) console.warn('Header section not found');
      
      const sections = {
        userProfile: clone.querySelector('.user-profile'),
        vitamins: clone.querySelector('.vitamins-section'),
        minerals: clone.querySelector('.minerals-section'),
        micronutrients: clone.querySelector('.micronutrient-section'),
        explanations: clone.querySelector('.nutrient-explanations'),
        disclaimer: clone.querySelector('.disclaimer')
      };

      // Verify all sections exist
      Object.entries(sections).forEach(([name, section]) => {
        if (!section) console.warn(`${name} section not found`);
      });

      // Handle logo image
      let logoDataUrl = null;
      if (header) {
        const logoImg = header.querySelector('img');
        if (logoImg) {
          try {
            logoDataUrl = await getImageDataUrl(logoImg.src);
            console.log('Logo converted to data URL successfully');
          } catch (imgError) {
            console.error('Failed to process logo image:', imgError);
            // Fallback to alt text if image fails
            logoImg.style.display = 'none';
          }
        }
      }

      // Create PDF page template
      const createPage = (content, pageNumber, totalPages) => {
        const page = document.createElement('div');
        page.className = 'pdf-page';
        page.style.padding = '12mm';
        page.style.boxSizing = 'border-box';
        page.style.height = '297mm';
        page.style.display = 'flex';
        page.style.flexDirection = 'column';
        page.style.overflow = 'hidden'; // Prevent content overflow
        
        // Add header to every page
        if (header) {
          const headerClone = header.cloneNode(true);
          const logo = headerClone.querySelector('img');
          if (logo) {
            logo.style.height = '80px';
            logo.style.width = '80px';
            logo.style.marginBottom = '10px';
            if (logoDataUrl) {
              logo.src = logoDataUrl;
            }
          }
          page.appendChild(headerClone);
        }
        
        // Main content container
        const contentContainer = document.createElement('div');
        contentContainer.style.flexGrow = '1';
        contentContainer.style.overflow = 'hidden';
        contentContainer.appendChild(content.cloneNode(true));
        page.appendChild(contentContainer);
        
        // Footer
        const footer = document.createElement('div');
        footer.style.marginTop = 'auto';
        footer.style.paddingTop = '5mm';
        footer.style.textAlign = 'center';
        footer.style.fontSize = '10px';
        footer.style.color = '#666';
        footer.style.borderTop = '1px solid #eee';
        footer.textContent = `Page ${pageNumber} of ${totalPages} • © ${new Date().getFullYear()} Nutrition and Vitamin Guru`;
        page.appendChild(footer);
        
        return page;
      };

      // Build pages with fallbacks for missing sections
      const pagesToRender = [];
      
      // Page 1: User Profile + Vitamins
      if (sections.userProfile || sections.vitamins) {
        const page1Content = document.createElement('div');
        if (sections.userProfile) page1Content.appendChild(sections.userProfile.cloneNode(true));
        if (sections.vitamins) page1Content.appendChild(sections.vitamins.cloneNode(true));
        pagesToRender.push(createPage(page1Content, 1, 4));
      }

      // Page 2: Minerals + Micronutrients
      if (sections.minerals || sections.micronutrients) {
        const page2Content = document.createElement('div');
        if (sections.minerals) page2Content.appendChild(sections.minerals.cloneNode(true));
        if (sections.micronutrients) page2Content.appendChild(sections.micronutrients.cloneNode(true));
        pagesToRender.push(createPage(page2Content, 2, 4));
      }

      // Page 3: Explanations
      if (sections.explanations) {
        pagesToRender.push(createPage(sections.explanations, 3, 4));
      }

      // Page 4: Disclaimer
      if (sections.disclaimer) {
        pagesToRender.push(createPage(sections.disclaimer, 4, 4));
      }

      if (pagesToRender.length === 0) {
        throw new Error('No content available to generate PDF');
      }

      // Add all pages to print container
      pagesToRender.forEach(page => printContainer.appendChild(page));

      // Generate PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        hotfixes: ["px_scaling"] // Fix for pixel scaling issues
      });

      // Process each page
      for (let i = 0; i < pagesToRender.length; i++) {
        const pageElement = pagesToRender[i];
        
        try {
          const canvas = await html2canvas(pageElement, {
            scale: 2,
            useCORS: true,
            allowTaint: false, // Keep false unless absolutely necessary
            width: pageElement.offsetWidth,
            height: pageElement.offsetHeight,
            scrollX: 0,
            scrollY: 0,
            logging: true,
            onclone: (clonedDoc) => {
              // Ensure all images are visible in the clone
              clonedDoc.querySelectorAll('img').forEach(img => {
                if (!img.complete || img.naturalHeight === 0) {
                  img.style.display = 'none';
                }
              });
            }
          });

          if (i > 0) pdf.addPage();
          pdf.addImage(canvas, 'PNG', 0, 0, 210, 297);
        } catch (pageError) {
          console.error(`Error rendering page ${i + 1}:`, pageError);
          // Skip failed page but continue with others
          continue;
        }
      }

      // Finalize PDF
      if (pdf.internal.getNumberOfPages() > 0) {
        pdf.save(`${name || 'nutrition'}-report.pdf`);
      } else {
        throw new Error('PDF generation failed - no pages were created');
      }

    } catch (error) {
      console.error('PDF generation failed:', error);
      alert(`PDF generation failed: ${error.message || 'Unknown error'}`);
    } finally {
      if (printContainer && document.body.contains(printContainer)) {
        document.body.removeChild(printContainer);
      }
      setIsExporting(false);
    }
};

// Robust image loader with timeout
function getImageDataUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    let timer = setTimeout(() => {
      reject(new Error('Image load timeout'));
      img.onload = img.onerror = null;
    }, 5000);

    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = (err) => {
      clearTimeout(timer);
      reject(err);
    };
    img.src = url;
  });
}


  const PaginationControls = ({ currentPage, totalPages, onPageChange }) => (
    <div className="pagination-container no-print">
      <button
        className="page-button"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Previous
      </button>
      
      {Array.from({ length: totalPages }, (_, i) => (
        <button
          key={i}
          className={`page-button ${currentPage === i + 1 ? 'active' : ''}`}
          onClick={() => onPageChange(i + 1)}
        >
          {i + 1}
        </button>
      ))}
      
      <button
        className="page-button"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </button>
    </div>
  );

  return (
    <motion.div
      style={{
        padding: isMobile ? "1rem" : "1.5rem",
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
        fontFamily: "sans-serif"
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <header
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#0284c7',
          color: 'white',
          padding: isMobile ? '0.75rem' : '1rem 1.5rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          borderRadius: '1rem',
          marginBottom: '2rem',
          height: 'auto',
          flexWrap: 'wrap',
        }}
      >
        {/* Logo - Left */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          marginBottom: isMobile ? '0.5rem' : 0
        }}>
          <img
            src={logoImage}
            alt="Nutrition Logo"
            style={{
              height: isMobile ? '60px' : '80px',
              width: isMobile ? '60px' : '80px',
              objectFit: 'contain',
              borderRadius: '2rem',
              marginRight: isMobile ? '0.5rem' : '1rem'
            }}
          />
        </div>

        {/* Text Content - Centered */}
        <div style={{ 
          flex: 1, 
          textAlign: 'center',
          padding: isMobile ? '0.5rem 0' : 0
        }}>
          <h1
            style={{
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              fontWeight: 'bold',
              margin: 0,
              lineHeight: 1.2
            }}
          >
            Comprehensive Daily Nutrient Intake & RDI Calculator
          </h1>
          <p
            style={{
              fontSize: isMobile ? '0.875rem' : '1rem',
              color: '#f3f4f6',
              marginTop: '0.5rem',
              marginBottom: 0
            }}
          >
            Based on USDA Dietary Reference Intakes (DRIs) for the United States
          </p>
        </div>
      </header>

      {calculated === false ? (
        <section style={{
          border: '1px solid #d1d5db',
          backgroundColor: 'white',
          padding: isMobile ? '1rem' : '1.5rem',
          borderRadius: isMobile ? '1.5rem' : '5rem',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          flexShrink: 'inherit',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
        }}>
          {/* How-to Guide (Collapsible) */}
          <details style={{ 
            marginBottom: '1.5rem',
            border: '1px solid #e2e8f0',
            borderRadius: isMobile ? '1rem' : '2rem',
            overflow: 'hidden'
          }}>
            <summary style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#f8fafc',
              cursor: 'pointer',
              borderRadius: isMobile ? '1rem' : '2rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: '500',
              color: '#1e40af',
              listStyle: 'none'
            }}>
              <FaQuestionCircle />
              <span>How to Use This Calculator</span>
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </summary>
            <div style={{ 
              padding: '1rem', 
              backgroundColor: 'white', 
              borderRadius: isMobile ? '1rem' : '2rem'
            }}>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
                <div style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>1</div>
                <p style={{ margin: 0 }}>
                  <strong>Use accurate measurements</strong> - Weigh yourself in the morning before eating
                </p>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
                <div style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>2</div>
                <p style={{ margin: 0 }}>
                  <strong>Be honest about activity</strong> - Select your actual exercise level, not goals
                </p>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>3</div>
                <p style={{ margin: 0 }}>
                  <strong>Review special conditions</strong> - Pregnancy/breastfeeding affects requirements
                </p>
              </div>
            </div>
          </details>
          
          <h2 style={{
            color: '#0ea5e9',
            fontSize: isMobile ? '1rem' : '1.125rem',
            fontWeight: '600',            
            paddingBottom: '0.2rem',
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            justifyContent: 'center',
            marginBottom: '1.5rem',
            alignItems: 'center'
          }}>
            <FaChartBar style={{paddingRight: '.55rem'}} /> User Profile
          </h2>  

          <div style={{
            display: 'block',
            gridTemplateColumns: '1fr 1fr',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '2rem'
          }}>
            {/* Responsive Container */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: '1rem',
              width: '100%'
            }}>
              {/* Name Input */}
              <div style={{ 
                flex: '1 1 200px',
                minWidth: '200px'
              }}>
                <label style={{ 
                  color: '#374151', 
                  fontWeight: '500', 
                  marginBottom: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '0.55rem',
                }}>
                  <FaUser style={{paddingRight: '0.25rem'}} />
                  Name <InfoIcon 
                    content="Use your full name for personalized results" 
                    mobile={isMobile}
                  />
                </label>
                <input
                  type="text"
                  placeholder="Enter your name (3-32 characters)"
                  value={name}
                  onMouseEnter={() => handleHover('formFields', 'nameInput', true)}
                  onMouseLeave={() => handleHover('formFields', 'nameInput', false)}
                  onTouchStart={() => handleHover('formFields', 'nameInput', true)}
                  onTouchEnd={() => handleHover('formFields', 'nameInput', false)}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (input.length <= 32) {
                      setName(input);
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value.length < 3) {
                      e.target.setCustomValidity("Name must be at least 3 characters");
                    } else {
                      e.target.setCustomValidity("");
                    }
                  }}
                  style={{
                    width: '100%',
                    border: `1px solid ${
                      name.length > 0 && name.length < 3 
                        ? '#DC3545'
                        : '#d1d5db'
                    }`,
                    borderRadius: '0.75rem',
                    padding: '0.75rem 1rem',
                    outline: 'none',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                    boxShadow: hoverStates.formFields.nameInput ? '0 0 0 2px #bfdbfe' : 'none',
                    borderColor: hoverStates.formFields.nameInput ? '#3b82f6' : '#d1d5db'
                  }}
                  minLength={3}
                  maxLength={32}
                  required
                />
                
                {name.length > 0 && name.length < 3 && (
                  <div style={{
                    color: '#DC3545',
                    fontSize: '0.875rem',
                    marginTop: '-0.25rem'
                  }}>
                    Name must be at least 3 characters
                  </div>
                )}
                {name.length === 32 && (
                  <div style={{
                    color: '#6B7280',
                    fontSize: '0.875rem',
                    marginTop: '-0.25rem'
                  }}>
                    Maximum 32 characters reached
                  </div>
                )}
              </div>
              
              {/* Age Input */}
              <div style={{ 
                flex: '1 1 200px',
                minWidth: '200px'
              }}>
                <label style={{ 
                  color: '#374151', 
                  fontWeight: '500', 
                  marginBottom: '0.25rem', 
                  display: 'flex',
                  paddingLeft: '0.55rem'
                }}>
                  Age (years): <InfoIcon 
                    content="Age affects nutrient absorption rates" 
                    mobile={isMobile}
                  />
                </label>
                <input
                  type="number"
                  id="ageInput"
                  value={age}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val=== ''){
                      setAge('');
                    } else {
                      const num= parseInt(val);
                      if(!isNaN (num)) setAge(num);
                      }
                    }}
                  min="1"
                  max="120"
                  onMouseEnter={() => handleHover('formFields', 'ageInput', true)}
                  onMouseLeave={() => handleHover('formFields', 'ageInput', false)}
                  onTouchStart={() => handleHover('formFields', 'ageInput', true)}
                  onTouchEnd={() => handleHover('formFields', 'ageInput', false)}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value);
                    if (isNaN(val) || val < 1 || val > 120) {
                      e.target.setCustomValidity("Please enter a valid age between 1 and 120");
                    } else {
                      e.target.setCustomValidity("");
                    }
                  }}
                  style={{
                    boxShadow: hoverStates.formFields.ageInput ? '0 0 0 2px #bfdbfe' : 'none',
                    borderColor: hoverStates.formFields.ageInput ? '#3b82f6': 
                              (document.getElementById('ageInput')?.validity.rangeUnderflow || 
                               document.getElementById('ageInput')?.validity.rangeOverflow) ? '#DC3545' : '#d1d5db',
                    width: '100%',
                    border: '1px solid',
                    borderRadius: '0.75rem',
                    padding: '0.75rem 1rem',
                    outline: 'none',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    <FaInfoCircle style={{ marginRight: '0.25rem' }} />
                    Children and seniors have different requirements
                  </div>
                  <div style={{
                    color: '#DC3545',
                    fontSize: '0.875rem',
                    marginTop: '0.25rem',
                    height: '1rem',
                    visibility: document.getElementById('ageInput')?.validity.rangeUnderflow || 
                              document.getElementById('ageInput')?.validity.rangeOverflow ? 'visible' : 'hidden'
                  }}>
                    Please enter a valid age (1-120)
                  </div>
                </div>
              </div>
              
              {/* Weight Input */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '0.15rem',
                paddingTop: '0.15rem'
              }}>
                <label style={{ 
                  color: '#374151', 
                  fontWeight: '500', 
                  marginBottom: '0.25rem', 
                  paddingLeft: '0.55rem'
                }}><FaWeightScale style={{ paddingRight: '0.35rem'}}/>
                  Weight (kg)
                </label>
                <input
                  type="number"
                  id="weightInput"
                  placeholder="Enter weight in kg"
                  value={weight}
                  onChange={(e) => {
                    const val = e.target.value;
    if (val === '') {
      setWeight(''); // allow empty string while typing
    } else {
      const num = parseFloat(val);
      if (!isNaN(num)) setWeight(num);
    }
  }}
  min="1"
  max="300"
  step="0.1"
                  onMouseEnter={() => handleHover('formFields', 'weightInput', true)}
                  onMouseLeave={() => handleHover('formFields', 'weightInput', false)}
                  onTouchStart={() => handleHover('formFields', 'weightInput', true)}
                  onTouchEnd={() => handleHover('formFields', 'weightInput', false)}
                  onBlur={(e) => {
                    const weight = parseFloat(e.target.value);
                    if (isNaN(weight) || weight < 1 || weight > 300) {
                      e.target.setCustomValidity("Please enter a valid weight between 1 and 300 kg");
                    } else {
                      e.target.setCustomValidity("");
                    }
                  }}
                  style={{
                    boxShadow: hoverStates.formFields.weightInput ? '0 0 0 2px #bfdbfe' : 'none',
                    borderColor: hoverStates.formFields.weightInput ? '#3b82f6' : '#d1d5db',
                    width: '100%',
                    border: '1px solid',
                    borderRadius: '0.75rem',
                    padding: '0.75rem 1rem',
                    outline: 'none',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              {/* Height Input */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '0.25rem',
                paddingTop: '0.25rem'
              }}> 
                <label style={{ 
                  color: '#374151', 
                  fontWeight: '500', 
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '0.25rem',
                  paddingLeft: '0.25rem'
                }}>
                  <FaRulerVertical/>
                  Height (cm)
                </label>
                <input
                  type="number"
                  id="heightInput"
                  placeholder="Enter height in cm"
                  value={height}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setHeight(''); // allow clearing
                    } else {
                      const num = parseInt(val);
                      if (!isNaN(num)) setHeight(num);
                    }
                  }}
                  min="50"
                  max="300"
                  onMouseEnter={() => handleHover('formFields', 'heightInput', true)}
                  onMouseLeave={() => handleHover('formFields', 'heightInput', false)}
                  onTouchStart={() => handleHover('formFields', 'heightInput', true)}
                  onTouchEnd={() => handleHover('formFields', 'heightInput', false)}
                  onBlur={(e) => {
                    const height = parseInt(e.target.value);
                    if (isNaN(height) || height < 50 || height > 300) {
                      e.target.setCustomValidity("Please enter a valid height between 50 and 300 cm");
                    } else {
                      e.target.setCustomValidity("");
                    }
                  }}
                  style={{
                    boxShadow: hoverStates.formFields.heightInput ? '0 0 0 2px #bfdbfe' : 'none',
                    borderColor: hoverStates.formFields.heightInput ? '#3b82f6' : '#d1d5db',
                    width: '100%',
                    border: '1px solid',
                    borderRadius: '0.75rem',
                    padding: '0.75rem 1rem',
                    outline: 'none',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Gender and Pregnancy Status */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                width: '100%'
              }}>
                <div style={{ 
                  flex: '1 1 200px',
                  minWidth: '200px'
                }}>
                  <label style={{ 
                    color: '#374151', 
                    fontWeight: '500', 
                    marginBottom: '0.35rem',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '0.25rem',
                    paddingTop:'0.55rem'
                  }}>
                    <FaVenusMars style={{ paddingRight: '0.25rem'}}/>Gender
                  </label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    onMouseEnter={() => handleHover('formFields', 'genderInput', true)}
                    onMouseLeave={() => handleHover('formFields', 'genderInput', false)}
                    onTouchStart={() => handleHover('formFields', 'genderInput', true)}
                    onTouchEnd={() => handleHover('formFields', 'genderInput', false)}
                    style={{
                      boxShadow: hoverStates.formFields.genderInput ? '0 0 0 2px #bfdbfe' : 'none',
                      borderColor: hoverStates.formFields.genderInput ? '#3b82f6' : '#d1d5db',
                      width: '100%',
                      border: '1px solid',
                      borderRadius: '0.75rem',
                      padding: '0.75rem 1rem',
                      outline: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                {gender === 'female' && (
                  <div style={{
                    display: 'grid',
                    flexDirection: 'column',
                    gap: '1rem',
                    marginBottom: '0.15rem',
                    gridColumn: '1 / -1'
                  }}>
                    <div style={{ width: '100%' }}>
                      <label style={{ 
                        fontWeight: '500',
                        paddingLeft:'0.35rem', 
                        color: '#374151',
                        marginBottom: '0.25rem'  
                      }}>
                        Pregnancy Status:  
                        <InfoIcon 
                          content="Additional nutrients are required during pregnancy/breastfeeding" 
                          mobile={isMobile}
                        />
                      </label>
                      <select
                        value={pregnancyStatus}
                        onChange={(e) => setPregnancyStatus(e.target.value)}
                        onMouseEnter={() => handleHover('formFields', 'pregnancyInput', true)}
                        onMouseLeave={() => handleHover('formFields', 'pregnancyInput', false)}
                        onTouchStart={() => handleHover('formFields', 'pregnancyInput', true)}
                        onTouchEnd={() => handleHover('formFields', 'pregnancyInput', false)}
                        style={{
                          boxShadow: hoverStates.formFields.pregnancyInput ? '0 0 0 2px #bfdbfe' : 'none',
                          borderColor: hoverStates.formFields.pregnancyInput ? '#3b82f6' : '#d1d5db',
                          width: '100%',
                          border: '1px solid',
                          borderRadius: '0.75rem',
                          padding: '0.75rem 1rem',
                          outline: 'none',
                          fontSize: '1rem',
                          boxSizing: 'border-box',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          gap:'0.15rem'
                        }}
                      >
                        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                          <FaInfoCircle style={{ marginRight: '0.25rem' }} />
                          Includes adjustments for {pregnancyStatus === 'pregnancy' ? 'fetal development' : 'milk production'}
                        </div>
                        <option value="none">None</option>
                        <option value="pregnancy">Pregnant</option>
                        <option value="lactation">Breastfeeding</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Lifestyle */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '0.15rem'
              }}>
                <label htmlFor="lifestyle" style={{ 
                  color: '#374151', 
                  fontWeight: '500', 
                  marginBottom: '0.35rem',
                  display: 'flex',
                  marginTop: isMobile ? '0.5rem' : '1rem',
                  paddingTop: isMobile ? '0' : '0.35rem',
                  paddingLeft:'0.35rem',
                  alignItems: 'center'
                }}>
                  <FaBriefcase style={{ marginRight: '0.5rem' }} />
                  Lifestyle (Occupation):
                  <InfoIcon
                    content={
                      <div style={{
                        width: isMobile ? '280px' : '380px',
                        padding: '12px',
                        borderRadius: '8px',
                        background: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: '1px solid #e2e8f0',
                        maxHeight: 'min(400px, 70vh)',
                        overflowY: 'auto'
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',  
                          gap: '12px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            paddingBottom: '8px',
                            marginBottom: '8px',
                            borderBottom: '1px solid #f3f4f6',
                            position: 'sticky',
                            top: 0,
                            background: 'white',
                            zIndex: 1
                          }}>
                            <FaUserTie style={{ 
                              color: '#3b82f6',
                              fontSize: '14px'
                            }} />
                            <span style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#111827'
                            }}>
                              Lifestyle Roles
                            </span>
                          </div>

                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(100px, 1fr))',
                            gap: '12px 8px',
                            paddingBottom: '8px',
                            alignItems: 'start',
                            maxHeight: '280px',
                            overflowY: 'auto',
                            paddingRight: '4px',
                            scrollbarWidth: 'thin'
                          }}>
                            {[
                              {
                                icon: <FaLaptop style={{ color: '#6366f1' }} />,
                                role: 'Office',
                                desc: 'Desk-based work with minimal exertion'
                              },
                              {
                                icon: <FaMedal style={{ color: '#f59e0b' }} />,
                                role: 'Athlete',
                                desc: 'Professional training regimen'
                              },
                              {
                                icon: <FaHammer style={{ color: '#ef4444' }} />,
                                role: 'Laborer',
                                desc: 'Physical jobs/Construction/farming jobs'
                              },
                              {
                                icon: <FaBook style={{ color: '#10b981' }} />,
                                role: 'Student',
                                desc: 'Variable activity'
                              },
                              {
                                icon: <FaUmbrellaBeach style={{ color: '#8b5cf6' }} />,
                                role: 'Retired',
                                desc: 'Post-employment lifestyle'
                              }
                            ].map((item, index) => (
                              <div 
                                key={index}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px'
                                }}
                              >
                                <div style={{
                                  display:'flex',
                                  alignItems:'center',
                                  gap:'6px'
                                }}>
                                  <div style={{ color: '#6366f1' }}>{item.icon}</div>
                                  <span style={{ fontSize: '13px', fontWeight: '500' }}>{item.role}</span>
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#6b7280',
                                  lineHeight: '1.4',
                                  marginTop: '11px'
                                }}>
                                  {item.desc}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div style={{
                            padding: '8px',
                            background: '#f8fafc',
                            borderRadius: '6px',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            position: 'sticky',
                            bottom: 0
                          }}>
                            <FaInfoCircle style={{ 
                              fontSize: '12px',
                              color: '#9ca3af'
                            }} />
                            <span>Customizes nutrient recommendations</span>
                          </div>
                        </div>
                      </div>
                    }
                    placement={isMobile ? "bottom" : "top"}
                    popperOptions={{
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            padding: 8,
                            boundariesElement: 'viewport',
                            altAxis: true
                          }
                        },
                        {
                          name: 'flip',
                          options: {
                            fallbackPlacements: isMobile ? ['top', 'right', 'left'] : ['bottom', 'right', 'left'],
                            flipVariations: false
                          }
                        },
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 12]
                          }
                        }
                      ]
                    }}
                  />
                </label>
                <select
                  id="lifestyle"
                  value={lifestyle}
                  onChange={(e) => setLifestyle(e.target.value)}
                  onMouseEnter={() => handleHover('formFields', 'lifestyleInput', true)}
                  onMouseLeave={() => handleHover('formFields', 'lifestyleInput', false)}
                  onTouchStart={() => handleHover('formFields', 'lifestyleInput', true)}
                  onTouchEnd={() => handleHover('formFields', 'lifestyleInput', false)}
                  style={{
                    boxShadow: hoverStates.formFields.lifestyleInput ? '0 0 0 2px #bfdbfe' : 'none',
                    borderColor: hoverStates.formFields.lifestyleInput ? '#3b82f6' : '#d1d5db',
                    width: '100%',
                    border: '1px solid',
                    borderRadius: '0.75rem',
                    padding: '0.75rem 1rem',
                    outline: 'none',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                >
                  {lifestyleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Activity Level */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '0.15rem'
              }}>
                <label htmlFor="activityLevel" style={{ 
                  color: '#374151', 
                  fontWeight: '500', 
                  marginBottom: '0.35rem',
                  display: 'flex',
                  marginTop: isMobile ? '0.5rem' : '0.6rem',
                  paddingTop: isMobile ? '0' : '0.35rem',
                  paddingLeft:'0.35rem',
                  alignItems: 'center'
                }}>
                  <FaWalking style={{ marginRight: '0.5rem' }} />
                  Activity Level: 
                  <InfoIcon
                    content={
                      <div style={{
                        width: isMobile ? '280px' : '340px',
                        padding: '12px',
                        borderRadius: '8px',
                        background: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: '1px solid #e2e8f0',
                        maxHeight: 'min(300px, 60vh)',
                        overflow: 'hidden'
                      }}>
                        {/* Header */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          paddingBottom: '8px',
                          marginBottom: '8px',
                          borderBottom: '1px solid #f3f4f6'
                        }}>
                          <FaRunning style={{ color: '#10b981', fontSize: '16px' }} />
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                            Activity Intensity Levels
                          </span>
                        </div>

                        {/* Grid */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(100px, 1fr))',
                          gap: '12px 8px',
                          marginBottom: '12px'
                        }}>
                          {[
                            {
                              level: 'Sedentary',
                              icon: <FaCouch style={{ color: '#9ca3af' }} />,
                              desc: 'Little to no exercise',
                              examples: 'Office work, mostly sitting'
                            },
                            {
                              level: 'Moderate',
                              icon: <FaWalking style={{ color: '#3b82f6' }} />,
                              desc: '3-5 workouts/week',
                              examples: 'Brisk walking, light cycling'
                            },
                            {
                              level: 'High',
                              icon: <FaRunning style={{ color: '#ef4444' }} />,
                              desc: 'Daily vigorous exercise',
                              examples: 'Running, HIIT, sports'
                            },
                            {
                              level: 'Extreme',
                              icon: <FaTrophy style={{ color: '#f59e0b' }} />,
                              desc: '2x daily training',
                              examples: 'Pro athletes, endurance'
                            }
                          ].map((item, index) => (
                            <div key={index} style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                {item.icon}
                                <span style={{
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  color: '#1f2937'
                                }}>
                                  {item.level}
                                </span>
                              </div>
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                lineHeight: '1.4',
                                paddingLeft: '24px'
                              }}>
                                {item.desc}
                              </div>
                              <div style={{
                                fontSize: '11px',
                                color: '#9ca3af',
                                fontStyle: 'italic',
                                paddingLeft: '24px'
                              }}>
                                {item.examples}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Footer Note */}
                        <div style={{
                          padding: '8px',
                          background: '#f8fafc',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#4b5563',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <FaInfoCircle style={{ color: '#9ca3af' }} />
                          <span>Combines with your Lifestyle occupation</span>
                        </div>
                      </div>
                    }
                    placement={isMobile ? "bottom" : "top"}
                    popperOptions={{
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            padding: 8,
                            boundariesElement: 'viewport',
                            altAxis: true
                          }
                        },
                        {
                          name: 'flip',
                          options: {
                            fallbackPlacements: isMobile ? ['top', 'right'] : ['bottom', 'right'],
                            flipVariations: false
                          }
                        },
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 12]
                          }
                        }
                      ]
                    }}
                  />
                </label>
                <select
                  id="activityLevel"
                  value={activityLevel}
                  onChange={(e) => setActivityLevel(e.target.value)}
                  onMouseEnter={() => handleHover('formFields', 'activityInput', true)}
                  onMouseLeave={() => handleHover('formFields', 'activityInput', false)}
                  onTouchStart={() => handleHover('formFields', 'activityInput', true)}
                  onTouchEnd={() => handleHover('formFields', 'activityInput', false)}
                  style={{
                    boxShadow: hoverStates.formFields.activityInput ? '0 0 0 2px #bfdbfe' : 'none',
                    borderColor: hoverStates.formFields.activityInput ? '#3b82f6' : '#d1d5db',
                    width: '100%',
                    border: '1px solid',
                    borderRadius: '0.75rem',
                    padding: '0.75rem 1rem',
                    outline: 'none',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                >
                  {activityLevelOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Calculate Button */}
              <div className="calculate-buttons" style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 10,
                gap: '1rem',
                marginTop: '1.5rem',
                width: '100%'
              }}>
                <button
                  onClick={calculateRequirements}
                  disabled={isLoading}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '2rem',
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isLoading || name.length<3 ? 'not-allowed' : 'pointer',
                    opacity: isLoading || name.length<3  ? 0.7 : 1,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transform: calculatePressed ? 'scale(0.97)' : 'scale(1)',
                    boxShadow: isHovered ? '0 0 0 2px #dbeafe' : 'none',
                    width: isMobile ? '100%' : 'auto',
                    minWidth: '200px'
                  }}
                  onMouseDown={() => setCalculatePressed(true)}
                  onMouseUp={() => setCalculatePressed(false)}
                  onMouseLeave={() => {
                    setCalculatePressed(false);
                    setIsHovered(false);
                  }}
                  onMouseEnter={() => setIsHovered(true)}
                  onTouchStart={()=> setIsHovered(true)}
                >
                  {isLoading ? (
                    <>
                      <ClipLoader size={20} color="#ffffff" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <FaCalculator />
                      Calculate RDI
                    </>
                  )}
                </button>
              </div>
            </div>
        </section>
      ) : (  
                
        <div
        ref={pdfRef}
        className="printable-content"
        style={{
          backgroundColor: 'white',
          color: '#1e40af',
          padding: isMobile ? '1rem' : '2rem',
          border: '1px solid #e5e7eb',
          borderRadius: isMobile ? '1.5rem' : '5rem',
          marginBottom: '2rem',
          fontFamily: 'Arial, sans-serif',
          maxWidth: '800px',
          margin: '0 auto',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* Header */}
        <section>
          <div className="header"
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              borderBottom: '2px solid #00aded',
              backgroundColor: 'white',
              paddingBottom: '.1rem',
              marginBottom: '.5rem',
            }}
          >
            <img
              src={logoImage}
              alt="Nutrition Logo"
              style={{
                height:  '80px',
                width: '80px',
                objectFit: 'contain',
                borderRadius: '2rem',
                marginRight: isMobile ? '0' : '1rem',
                marginBottom: isMobile ? '0.5rem' : '0',
              }}
            />
            <div
              style={{
                textAlign: 'center',
                marginRight: isMobile ? '0' : '5rem',
                flex: 1,
              }}>
              <h1
                style={{
                  fontSize: isMobile ? '1.25rem' : '1.625rem',
                  fontWeight: '700',
                  margin: 0,
                  color: '#0284c7',
                  lineHeight: 1.3,
                }}
              >
                Nutrition and Vitamin Guru
              </h1>
            
              <h2
                style={{
                  fontSize: isMobile ? '1.025rem' : '1.25rem',
                  fontWeight: '700',
                  margin: 0,
                  color: '#0284c7',
                  lineHeight: 1.3,
                }}
              >
                Recommended Dietary Intake Summary
              </h2>
              <h3
                style={{
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  marginTop: isMobile ? '0.5rem' : '0.9rem',
                  color: '#64748b',
                  fontWeight: '600',
                }}
              >
                Personalized RDI Report
              </h3>
            </div>
          </div>
        </section>
      
        <div className="user-profile" style={{ 
  marginBottom: '1rem',
  padding: isMobile ? '0 0.5rem' : '0'  // Add horizontal padding only on mobile
}}>
  <h2
    style={{
      fontSize: isMobile ? '1.125rem' : '1.375rem',
      fontWeight: '600',
      color: '#0ea5e9',
      marginBottom: '1.25rem',
      borderBottom: '1px solid #e2e8f0',
      paddingBottom: '0.5rem',
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'center',
      gap: '0.5rem',
      alignItems: 'center',
    }}
  >
    <FaUser /> User Profile Summary
  </h2>

  <div
    style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      flexWrap: 'wrap',
      gap: '0.75rem',
      justifyContent: 'center',
      alignItems: 'stretch',
    }}
  >
    {/* Name Box */}
    <div style={{
      flex: isMobile ? '0 0 calc(100% - 1rem)' : '1',
      minWidth: isMobile ? 'unset' : '200px',
      maxWidth: isMobile ? '100%' : 'unset',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      padding: '0.75rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      backgroundColor: 'white',
      boxSizing: 'border-box'  // Ensure padding is included in width calculation
    }}>
      <h5 style={{ margin: 0, textTransform: 'capitalize' }}><strong>NAME :</strong> {name}</h5>
    </div>

    {/* Age Box */}
    <div style={{
      flex: isMobile ? '0 0 calc(100% - 1rem)' : '1',
      minWidth: isMobile ? 'unset' : '200px',
      maxWidth: isMobile ? '100%' : 'unset',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      padding: '0.75rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      backgroundColor: 'white',
      boxSizing: 'border-box'
    }}>
      <h5 style={{ margin: 0 }}><strong>AGE :</strong> {age} years</h5>
    </div>

    {/* Gender Box */}
    <div style={{
      flex: isMobile ? '0 0 calc(100% - 1rem)' : '1',
      minWidth: isMobile ? 'unset' : '200px',
      maxWidth: isMobile ? '100%' : 'unset',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      padding: '0.75rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      backgroundColor: 'white',
      boxSizing: 'border-box'
    }}>
      <h5 style={{ margin: 0 }}><strong>GENDER :</strong> {gender === 'male' ? 'Male' : 'Female'}</h5>
    </div>

    {/* Weight Box */}
    <div style={{
      flex: isMobile ? '0 0 calc(100% - 1rem)' : '1',
      minWidth: isMobile ? 'unset' : '200px',
      maxWidth: isMobile ? '100%' : 'unset',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      padding: '0.75rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      backgroundColor: 'white',
      boxSizing: 'border-box'
    }}>
      <h5 style={{ margin: 0 }}><strong>WEIGHT :</strong> {weight} kg</h5>
    </div>

    {/* Height Box */}
    <div style={{
      flex: isMobile ? '0 0 calc(100% - 1rem)' : '1',
      minWidth: isMobile ? 'unset' : '200px',
      maxWidth: isMobile ? '100%' : 'unset',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      padding: '0.75rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      backgroundColor: 'white',
      boxSizing: 'border-box'
    }}>
      <h5 style={{ margin: 0 }}><strong>HEIGHT :</strong> {height} cm</h5>
    </div>

    {/* Conditional Pregnancy Box */}
    {gender === 'female' && pregnancyStatus !== 'none' && (
      <div style={{
        flex: isMobile ? '0 0 calc(100% - 1rem)' : '1',
        minWidth: isMobile ? 'unset' : '200px',
        maxWidth: isMobile ? '100%' : 'unset',
        border: '1px solid #e2e8f0',
        borderRadius: '0.5rem',
        padding: '0.75rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        backgroundColor: 'white',
        boxSizing: 'border-box'
      }}>
        <h5 style={{ margin: 0 }}>
          <strong>PREGNANCY STATUS :</strong>{' '}
          {pregnancyStatus === 'pregnancy' ? 'Pregnant' : 'Breastfeeding'}
        </h5>
      </div>
    )}

    {/* Lifestyle Box */}
    <div style={{
      flex: isMobile ? '0 0 calc(100% - 1rem)' : '1',
      minWidth: isMobile ? 'unset' : '200px',
      maxWidth: isMobile ? '100%' : 'unset',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      padding: '0.75rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      backgroundColor: 'white',
      boxSizing: 'border-box'
    }}>
      <h5 style={{ margin: 0 }}>
        <strong>LIFESTYLE :</strong>{' '}
        {lifestyleOptions.find((o) => o.value === lifestyle)?.label}
      </h5>
    </div>

    {/* Activity Level Box */}
    <div style={{
      flex: isMobile ? '0 0 calc(100% - 1rem)' : '1',
      minWidth: isMobile ? 'unset' : '200px',
      maxWidth: isMobile ? '100%' : 'unset',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      padding: '0.75rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      backgroundColor: 'white',
      boxSizing: 'border-box'
    }}>
      <h5 style={{ margin: 0 }}>
        <strong>ACTIVITY LEVEL :</strong>{' '}
        {activityLevelOptions.find((o) => o.value === activityLevel)?.label}
      </h5>
    </div>
  </div>
</div>      <div style={{ marginTop: '1rem' }}>

<div className="vitamins-section" style={{
  backgroundColor: '#f8fafc',
  padding: isMobile ? '0.15rem' : '0.3rem',
  borderRadius: isMobile ? '1rem' : '2rem',
  marginBottom: '1.5rem',  
  paddingBottom: '1rem',
  marginTop: '1rem',
  maxWidth: '55rem', // Constrain entire section width
   
}}>
  <h3 style={{ 
    color: '#3b82f6', 
    borderBottom: '1px solid #e5e7eb', 
    paddingBottom: '0.5rem',
    fontSize: isMobile ? '1rem' : '1.125rem',
    display: 'flex',
    justifyContent:'center',    
    alignItems: 'center',
    gap: '0.5rem',
    margin: '0 0.5rem 1rem' // Align with inner content
  }}>
    <FaBookMedical /> Vitamins
  </h3>
  <div style={{
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '1rem',
    padding: '0 0.8rem ',
    width: '100%', // Ensure it takes full available width
    boxSizing: 'border-box' // Include padding in width calculation
  }}>
    {Object.entries(nutritionalData[gender].vitamins).map(([vitamin, values]) => {
      const ageIndex = getAgeIndex();
      let value = values[ageIndex];
      if (gender === 'female' && pregnancyStatus !== 'none') {
        const special = nutritionalData[gender][pregnancyStatus]?.vitamins?.[vitamin]?.[ageIndex];
        if (special !== undefined) value = special;
      }
      const [name, unit] = vitamin.split(' (');
      const cleanUnit = unit?.replace(')', '') || '';

      return (
        <div key={vitamin} style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.5rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: '100%' // Ensure card doesn't overflow
        }}>
          <span style={{ 
            color: '#64748b',
            fontWeight: '500',
            fontSize: isMobile ? '0.85rem' : '0.9rem',
            marginRight: '0.5rem'
          }}>
            {name}:
          </span>
          <span style={{ 
            fontWeight: '600',
            color: '#1e293b',
            fontSize: isMobile ? '0.9rem' : '1rem',
            whiteSpace: 'nowrap'
          }}>
            {value} {cleanUnit}
          </span>
        </div>
      );
    })}
  </div> 
</div>

            {/* Minerals Section */}
            <div className="minerals-section" style={{
              backgroundColor: '#f8fafc',
              padding: isMobile ? '0.15rem' : '0.3rem',
              borderRadius: isMobile ? '1rem' : '2rem',
              marginTop: '1rem',
              marginBottom:'1.5rem',
              paddingBottom: '1rem',
              maxWidth: '55rem'
            }}>
              <h3 style={{ 
                color: '#3b82f6', 
                borderBottom: '1px solid #e5e7eb', 
                paddingBottom: '0.5rem',
                justifyContent:'center',                
                fontSize: isMobile ? '1rem' : '1.125rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
    
              }}>
                <FaAtom /> Minerals
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '1rem',
                padding: '0 0.8rem',
                boxSizing: 'border-box',
                width: '100%', // Ensure it takes full available width
    
                marginTop: '1rem'
              }}>
                {Object.entries(nutritionalData[gender].minerals).map(([mineral, values]) => {
                  const ageIndex = getAgeIndex();
                  const value = gender === 'female' && pregnancyStatus !== 'none' 
                    ? nutritionalData[gender][pregnancyStatus]?.minerals?.[mineral]?.[ageIndex] || values[ageIndex]
                    : values[ageIndex];
                  
                  const [name, unit] = mineral.split(' (');
                  const cleanUnit = unit?.replace(')', '') || '';

                  return (
                    <div key={mineral} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem',
                      backgroundColor: 'white',
                      borderRadius: '0.5rem',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <span style={{ 
                        color: '#64748b',
                        fontWeight: '500',
                        fontSize: isMobile ? '0.85rem' : '0.9rem'
                      }}>
                        {name}:
                      </span>
                      <span style={{ 
                        fontWeight: '600',
                        color: '#1e293b',
                        fontSize: isMobile ? '0.9rem' : '1rem'
                      }}>
                        {value} {cleanUnit}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Macronutrients Section */}
            <div className="micronutrient-section" style={{
              backgroundColor: '#f8fafc',
              padding: isMobile ? '0.15rem' : '0.3rem',
              borderRadius: isMobile ? '1rem' : '2rem',
              marginTop: '1rem',
              marginBottom:'1.5rem',
              paddingBottom: '1rem',
              maxWidth: '55rem'
            }}>
              <h3 style={{ 
                color: '#3b82f6', 
                borderBottom: '1px solid #e5e7eb', 
                paddingBottom: '0.5rem',
                fontSize: isMobile ? '1rem' : '1.125rem',
                display: 'flex',
                justifyContent:'center',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <FaWeightScale /> Macronutrients
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: ' 1rem',
                padding: '0 0.8rem',
                boxSizing: 'border-box',
                width: '100%', // Ensure it takes full available width
  
                marginTop: '1rem'
              }}>
                {Object.entries(nutritionalData[gender].weightBased).map(([nutrient, values]) => {
                  const ageIndex = getAgeIndex();
                  let value = values[ageIndex] * weight;
                  
                  if (gender === 'female' && pregnancyStatus !== 'none') {
                    const pregnancyValue = nutritionalData[gender][pregnancyStatus]?.weightBased?.[nutrient]?.[ageIndex];
                    if (pregnancyValue !== undefined) {
                      value = pregnancyValue * weight;
                    }
                  }
                  
                  // Apply activity multiplier to protein and water
                  if (nutrient === 'Protein (g)' || nutrient === 'Water (ml)') {
                    value *= activityMultipliers[activityLevel];
                  }
                  
                  // Additional breastfeeding adjustment for water
                  if (nutrient === 'Water (ml)' && pregnancyStatus === 'lactation') {
                    value += 500;
                  }
                  
                  const [name, unit] = nutrient.split(' (');
                  const cleanUnit = unit?.replace(')', '') || '';

                  return (
                    <div key={nutrient} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem',
                      backgroundColor: 'white',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      borderRadius: '0.5rem',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <span style={{ 
                        color: '#64748b',
                        fontWeight: '500',
                        fontSize: isMobile ? '0.85rem' : '0.9rem'
                      }}>
                        {name}:
                      </span>
                      <span style={{ 
                        fontWeight: '600',
                        color: '#1e293b',
                        fontSize: isMobile ? '0.9rem' : '1rem'
                      }}>
                        {Math.round(value)} {cleanUnit}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

      
        {/* Nutrient Explanation Section */}
        <details className="nutrient-explanations"
          style={{
            margin: '1.5rem 0',
            border: '1px solid #e2e8f0',
            borderRadius: isMobile ? '1rem' : '2rem',
          }}
        >
          <summary
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#f8fafc',
              cursor: 'pointer',
              borderRadius: isMobile ? '1rem' : '2rem',
              fontWeight: '500',
              color: '#1e40af',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <FaAtom /> Nutrient Explanations
          </summary>
          <div style={{ padding: '1rem', fontSize: isMobile ? '0.875rem' : '1rem' }}>
            <h4 style={{ marginTop: 0 }}>Key Nutrients</h4>
            <ul style={{ paddingLeft: '1.25rem' }}>
            <li    >
  <strong>Vitamin A:</strong> Supports vision, immune function, and skin health
</li>
<li    >
                  <strong>Vitamin C:</strong> Supports immune function and collagen production
                </li>
                <li    >
                  <strong>Vitamin D:</strong> Enhances calcium absorption (sunlight helps natural production)
                </li>
<li    >
  <strong>Vitamin E:</strong> Acts as an antioxidant, protects cells from damage
</li>
<li    >
  <strong>Vitamin K:</strong> Important for blood clotting and bone metabolism
</li>
<li    >
  <strong>Vitamin B1 (Thiamin):</strong> Helps convert food into energy and supports nerve function
</li>
<li    >
  <strong>Vitamin B2 (Riboflavin):</strong> Supports energy production and skin/eye health
</li>
<li    >
  <strong>Vitamin B3 (Niacin):</strong> Aids in digestion, skin health, and nervous system support
</li>
<li    >
  <strong>Vitamin B5 (Pantothenic Acid):</strong> Crucial for hormone production and fat metabolism
</li>
<li    >
  <strong>Vitamin B6 (Pyridoxine):</strong> Supports brain development and keeps immune system strong
</li>
<li    >
  <strong>Vitamin B7 (Biotin):</strong> Promotes healthy hair, skin, and nails
</li>
<li    >
  <strong>Vitamin B9 (Folate):</strong> Crucial for DNA synthesis and fetal development ({gender === 'female' ? 'essential during pregnancy' : 'supports cell production'})
</li>
<li    >
  <strong>Vitamin B12:</strong> Essential for red blood cell formation and nerve health (important for vegetarians)
</li>
<li    >
                  <strong>Iron:</strong> Carries oxygen in blood ({gender === 'female' ? 'women lose iron during menstruation' : 'men typically need less'})
                </li>
                <li    >
                  <strong>Calcium:</strong> Essential for bone health and muscle function
                </li>
<li    >
  <strong>Magnesium:</strong> Supports muscle/nerve function and steady heart rhythm
</li>
<li    >
  <strong>Zinc:</strong> Strengthens immune function and helps wounds heal
</li>
<li    >
  <strong>Potassium:</strong> Maintains fluid balance, heart function, and nerve signals
</li>
<li    >
  <strong>Sodium:</strong> Helps regulate blood pressure and fluid levels (excess intake should be avoided)
</li>
<li    >
  <strong>Choline:</strong> Supports brain and liver function (especially important during pregnancy)
</li>
<li    >
  <strong>Iodine:</strong> Essential for thyroid hormone production and metabolic regulation
</li>
<li    >
  <strong>Selenium:</strong> Protects against oxidative stress and supports thyroid health
</li>
<li    >
  <strong>Copper:</strong> Aids in iron absorption and red blood cell formation
</li>
<li    >
  <strong>Manganese:</strong> Supports bone development and antioxidant function
</li>

<li    >
  <strong>Omega-3 (DHA):</strong> Supports brain and heart health (especially important for children and pregnant women)
</li>

                <li    >
                  <strong>Protein:</strong> Builds and repairs tissues ({gender === 'male' ? 'higher needs for muscle maintenance' : 'especially important during pregnancy'})
                </li>
                
                
                

              {/* Add more as needed */}
            </ul>
          </div>
        </details>      

         {/* Disclaimer (Collapsible) */}
<details className="disclaimer" style={{ 
  margin: '1.5rem 0',
  border: '1px solid #fef3c7',
  borderRadius: isMobile ? '1rem' : '2rem',
  backgroundColor: '#fffbeb'
}}>
  <summary style={{
    padding: '0.75rem 1rem',
    backgroundColor: '#fffbeb',
    border: '1px solid #fef3c7',
    borderRadius: isMobile ? '1rem' : '2rem',
    cursor: 'pointer',
    fontWeight: '500',
    color: '#b45309',
    listStyle: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  }}>
    <FaExclamationTriangle style={{ marginRight: '0.5rem' }} />
    Important Disclaimer
  </summary>
  <div style={{ 
    padding: '1rem', 
    backgroundColor: 'white',
    fontSize: isMobile ? '0.875rem' : '1rem',
    color: '#92400e',
    lineHeight: 1.6
  }}>
    <p style={{ marginBottom: '0.75rem' }}>
      This report provides general recommended daily intake values based on your inputs. These are meant as guidelines only—actual individual needs may vary by ±15–20%.
    </p>
    <p style={{ marginBottom: '0.75rem' }}>
      This tool is <strong>not a substitute for professional medical advice, diagnosis, or treatment</strong>. Always consult a qualified healthcare provider before making changes to your diet, supplements, or lifestyle—especially if you:
    </p>
    <ul style={{ 
      paddingLeft: '1.25rem', 
      margin: '0 0 1rem 0',
      fontSize: isMobile ? '0.85rem' : '0.9rem'
    }}>
      <li style={{ marginBottom: '0.5rem' }}>Have diabetes or metabolic disorders</li>
      <li style={{ marginBottom: '0.5rem' }}>Have kidney or liver disease</li>
      <li style={{ marginBottom: '0.5rem' }}>Have food allergies or intolerances</li>
      <li style={{ marginBottom: '0.5rem' }}>Have a history of eating disorders</li>
      <li style={{ marginBottom: '0.5rem' }}>Have any chronic medical condition</li>
      <li style={{ marginBottom: '0.5rem' }}>Are taking medications that affect nutrient absorption</li>
    </ul>
    <p style={{ fontStyle: 'italic', fontSize: isMobile ? '0.8rem' : '0.875rem', color: '#78350f' }}>
      Last updated: April 2025 | Developed by NutrientRDI.ai
    </p>
  </div>
</details>
{/* 👇 Insert this PDF-Only Section after Disclaimer */}
<div style={{ display: 'none' }}>
        <div ref={printRef} id="pdf-content" style={{ width: '800px', padding: '2rem' }}>
          {/* PDF Page 1 */}
          <div style={{ pageBreakAfter: 'always' }}>
            <h1 style={{ textAlign: 'center' }}>Your Personalized Nutrition Report</h1>
            <p>Full Name: John Doe</p>
            <p>Age: 35 | Gender: Male</p>
            <p>Date: April 10, 2025</p>
            <hr />
            <h2>Vitamin Summary</h2>
            {/* Dynamic vitamin summary content */}
          </div>

          {/* PDF Page 2 */}
          <div style={{ pageBreakAfter: 'always' }}>
            <h2>Minerals & Micronutrients</h2>
            {/* Dynamic minerals & micronutrient content */}
          </div>

          {/* PDF Page 3 */}
          <div>
            <h2>Complete Nutrient Explanation</h2>
            <p>[Explanation content here]</p>
            <h3>Disclaimer</h3>
            <ul>
              <li>This report is for informational purposes only.</li>
              <li>Consult a healthcare provider before making dietary changes.</li>
              <li>Values are approximate and may vary.</li>
            </ul>
          </div>
        </div>
      </div>


          {/* Action Buttons */}
          <div className="action-buttons no-print" style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <button
              onClick={resetForm}
              onMouseDown={() => setIsPressed(true)}
              onMouseUp={() => setIsPressed(false)}
              onMouseLeave={() => {
                setIsPressed(false);
                setIsHovered(false);
              }}
              onMouseEnter={() => setIsHovered(true)}
              onTouchStart={()=> setIsHovered(true)}
              style={{
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: '1px solid',
                borderColor: isHovered ? '#93c5fd' : '#d1d5db',
                borderRadius: '0.75rem',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '1rem',
                transform: isPressed ? 'scale(0.97)' : 'scale(1)',
                boxShadow: isHovered ? '0 0 0 2px #dbeafe' : 'none',
                transition: 'transform 0.1s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              Start New Calculations
            </button>
            
            {/* Export Button with Loading State */}
            <button
              onClick={() => {
                if (!emailSubmitted) {
                  setShowEmailForm(true);
                } else {
                  handlePrint();
                }
              }}
              onMouseDown={() => setExportPressed(true)}
              onMouseUp={() => setExportPressed(false)}
              onMouseLeave={() => {
                setExportPressed(false);
                setExportHovered(false);
              }}
              onMouseEnter={() => setExportHovered(true)}
              onTouchStart={()=> setExportHovered(true)}
              style={{
                backgroundColor: exportHovered ? '#2563eb' : '#3b82f6',
                borderColor: exportHovered ?'#3b82f6' : '#d1d5db',
                boxShadow: exportHovered ?'0 0 0 2px #bfdbfe' : 'none',
                color: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                marginTop: '1rem',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
                transform: exportPressed ? 'scale(0.97)' : 'scale(1)',
                boxShadow: exportHovered ? '0 0 0 2px #dbeafe' : 'none',
                transition: 'transform 0.1s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                position: 'relative',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              {isExporting ? (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <ClipLoader size={18} color="#ffffff" />
                  Exporting...
                </div>
              ) : (
                <>
                  <FaFilePdf /> Export as PDF
                </>
              )}
            </button>
          </div>
          
          {showEmailForm && (
            <div
              className="email-form modal-overlay no-print"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
              style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }}
              onClick={() => setShowEmailForm(false)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'white',
                  padding: isMobile ? '1.5rem' : '2rem',
                  borderRadius: '0.75rem',
                  width: isMobile ? '90%' : '400px',
                  maxWidth: '400px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }}
              >
                <h3 id="modal-title" style={{
                  fontSize: isMobile ? '1.125rem' : '1.25rem',
                  marginBottom: '0.5rem',
                  color: '#0f172a'
                }}>
                  Get Your Nutrition Report
                </h3>
                <p style={{ 
                  fontSize: isMobile ? '0.875rem' : '0.95rem', 
                  color: '#475569' 
                }}>
                  Enter your email to receive your personalized report
                </p>

                <form onSubmit={handleEmailSubmit}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '0.375rem',
                      marginTop: '1rem',
                      marginBottom: '1rem',
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                  />

                  <div style={{ 
                    display: 'flex', 
                    gap: '1rem', 
                    justifyContent: 'flex-end',
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <button
                      type="button"
                      onClick={() => setShowEmailForm(false)}
                      style={{
                        background: '#f3f4f6',
                        color: '#1e293b',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.375rem',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: isMobile ? '0.875rem' : '1rem'
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={isSavingEmail}
                      style={{
                        background: isSavingEmail ? '#60a5fa' : '#3b82f6',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.375rem',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: isMobile ? '0.875rem' : '1rem'
                      }}
                    >
                      {isSavingEmail ? 'Saving...' : 'Get My PDF'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default NutritionalCalculator;